import { HelpIcon } from '@components/icons/HelpIcon';
import { Box, styled } from '@mui/material';
import { AccessPermissions } from '@types';
import { getKeycloakAdminUrl } from 'contexts/OidcContext/Oidc.utils';
import { memo } from 'react';
import { FormattedMessage } from 'react-intl';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { ROUTE_HELP, ROUTE_HOME, ROUTE_REQUESTS, ROUTE_SCHEDULE, ROUTE_SETTINGS } from 'routes';

import ClosePopup from '../../../assets/icons/close-popup.svg?react';
import UsersManagementIcon from '../../../assets/icons/Icon_User_Management.svg?react';
import LogoutIcon from '../../../assets/icons/icon-logout.svg?react';
import RequestsIcon from '../../../assets/icons/icon-requests.svg?react';
import ScheduleIcon from '../../../assets/icons/icon-schedule.svg?react';
import SettingsIcon from '../../../assets/icons/icon-settings.svg?react';
import { useOidc } from '../../../contexts/OidcContext/OidcContext';
import { useCurrentUser, useCurrentUserProfilePermissions } from '../../../contexts/OidcContext/OidcContext.selectors';
import { useUiValue } from '../../../contexts/UiContext';
import { useGoToSchedule } from '../../../hooks/useGoToSchedule';

const MenuHeader = styled('div')`
    height: 90px;
    width: 100%;
    color: white;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 33px 26px;
    font-family: Roboto, serif;
    font-size: 28px;
    font-weight: 300;
    line-height: 33px;
    border-bottom: 1px solid ${({ theme }) => theme.palette.blue.darkest};
`;

const MenuContent = styled('div')`
    font-family: Roboto, serif;
    font-size: 20px;
    font-weight: 300;
    letter-spacing: 0;
`;

const MenuItem = styled('div')<{ selected?: boolean; disabled?: boolean }>`
    display: flex;
    align-items: center;
    padding: 10px 25px;
    height: 72px;
    cursor: pointer;
    box-sizing: content-box;
    ${({ selected }) => (selected ? 'background: #ffffff30;' : '')}
    ${({ disabled }) => (disabled ? 'opacity: 0.5; cursor: default' : '')}
`;

const IconContainer = styled('div')`
    margin-right: 15px;
`;

const StyledMenu = styled('div')`
    color: ${({ theme }) => theme.palette.primary.contrastText};
    background: ${({ theme }) => theme.palette.blue.menuBg};
`;

export const Menu = memo(({ closeMenu }: { closeMenu?: () => void }) => {
    const navigate = useNavigate();
    const goToSchedule = useGoToSchedule();
    const currentUser = useCurrentUser();
    const selectedDate = useUiValue('selectedDate');
    const logout = useOidc().logout;
    const location = useLocation();
    const isAdmin = currentUser?.admin;
    const permissions = useCurrentUserProfilePermissions();
    const canGoToSchedule = permissions && permissions.includes(AccessPermissions.GANTT_PAGE);
    const canGoToRequests = permissions && permissions.includes(AccessPermissions.REQUEST_PAGE);

    function handleClickLogout() {
        logout({ redirectTo: 'home' });
    }

    const handleClickUsersManagement = () => {
        if (isAdmin) {
            window.open(getKeycloakAdminUrl(), '_blank');
            closeMenu?.();
        }
    };

    return (
        <StyledMenu>
            <MenuHeader>
                <Box>
                    {currentUser?.firstName}&nbsp;{currentUser?.lastName}
                </Box>
                <Box display="flex" alignItems="center" sx={{ cursor: 'pointer' }}>
                    <ClosePopup onClick={closeMenu} />
                </Box>
            </MenuHeader>
            <MenuContent onClick={closeMenu}>
                <MenuItem
                    onClick={() => canGoToSchedule && goToSchedule(selectedDate)}
                    selected={
                        !!matchPath(ROUTE_SCHEDULE, location.pathname) || !!matchPath(ROUTE_HOME, location.pathname)
                    }
                    disabled={!canGoToSchedule}
                >
                    <IconContainer>
                        <ScheduleIcon width="38" height="38" color="white" />
                    </IconContainer>
                    <FormattedMessage id="menu.schedule" />
                </MenuItem>
                <MenuItem
                    onClick={() => canGoToRequests && navigate(ROUTE_REQUESTS)}
                    selected={!!matchPath(ROUTE_REQUESTS, location.pathname)}
                    disabled={!canGoToRequests}
                >
                    <IconContainer>
                        <RequestsIcon width="38" height="38" color="white" />
                    </IconContainer>
                    <FormattedMessage id="menu.requests" />
                </MenuItem>
                <MenuItem
                    onClick={() => navigate(ROUTE_SETTINGS)}
                    selected={!!matchPath(ROUTE_SETTINGS, location.pathname)}
                >
                    <IconContainer>
                        <SettingsIcon width="38" height="38" color="white" />
                    </IconContainer>
                    <FormattedMessage id="menu.settings" />
                </MenuItem>

                {isAdmin && (
                    <MenuItem onClick={handleClickUsersManagement}>
                        <IconContainer>
                            <UsersManagementIcon width="38" height="38" color="white" />
                        </IconContainer>
                        <FormattedMessage id="menu.usersManagement" />
                    </MenuItem>
                )}

                <MenuItem onClick={() => navigate(ROUTE_HELP)} selected={!!matchPath(ROUTE_HELP, location.pathname)}>
                    <IconContainer>
                        <HelpIcon size={38} />
                    </IconContainer>
                    <FormattedMessage id="menu.help" />
                </MenuItem>
                <MenuItem onClick={handleClickLogout}>
                    <IconContainer>
                        <LogoutIcon width="38" height="38" color="white" />
                    </IconContainer>
                    <FormattedMessage id="menu.logout" />
                </MenuItem>
            </MenuContent>
        </StyledMenu>
    );
});
